import { formatDecimal, formatInteger, SUFFIX } from '@heviton/formatter';
import type { MarketItem, RecPoint, SmpPoint } from '@/shared/data-types';
import { UI_TEXT } from '@/shared/languages';

// KPX smp/rec → MarketItem[] — 날짜 내림차순 정렬 후 [0]=현재·[1]=전일, delta=현재-전일. 2건 미만이면 미표기
// (구앱 정합). SMP=평균가(avg)·REC=육지가(landAvgPrice). 대시보드(전체·단일)·수익 리포트의 `MarketCard` 공용 → shared/lib.

// 시장가 자릿수는 종목별로 다름 — SMP(원/kWh)=소수 2자리 · REC(원/REC)=정수. 현재가·전일대비 모두 같은 자릿수를
// 써야 한 행이 어긋나 보이지 않으므로 한 함수(`marketDigits`)로 묶는다.
function marketDigits(kind: MarketItem['kind'], value: number, suffix = ''): string {
  return kind === 'smp' ? formatDecimal(value, suffix, 2) : formatInteger(value, suffix);
}

/** 시장가 현재가 표기 — SMP=소수 2자리·REC=정수, 둘 다 '원' 단위. 시장가 Footer·카드 공용. */
export function marketPriceText(item: MarketItem): string {
  return marketDigits(item.kind, item.current, SUFFIX.WON);
}

/** 시장가 전일대비 표기 — 상승(↑)·하락(↓)·보합/결측('-'). 구앱 정합(0=보합). 시장가 Footer·카드 공용. */
export function marketDeltaText(item: MarketItem): string {
  const { delta } = item;
  if (delta == null || delta === 0) return UI_TEXT.COMMON_EMPTY_VALUE;
  return `${delta > 0 ? '↑' : '↓'} ${marketDigits(item.kind, Math.abs(delta))}`;
}

/** 등락 색조 — 상승=up(red)·하락=down(blue)·보합/결측=중립. 국내 증시 관습(`--heviton-up/down` SSOT). */
export function marketDeltaTone(delta: number | null): string {
  if (delta == null || delta === 0) return 'text-muted-foreground';
  return delta > 0 ? 'text-heviton-up' : 'text-heviton-down';
}

/** 기준일 'yyyy-MM-dd' → 'MM.DD'(결측/비정상 포맷이면 빈 문자열). 시장가 카드의 기준 시점 표기(구앱 정합). */
export function marketMonthDay(date: string): string {
  const [, month, day] = date.split('-');
  if (!month || !day) return '';
  return `${month}.${day}`;
}

function toMarketItem(
  kind: 'smp' | 'rec',
  list: { date: string; price: number }[],
): MarketItem | null {
  const sorted = [...list].sort((a, b) => b.date.localeCompare(a.date));
  const current = sorted[0];
  const previous = sorted[1];
  if (!current || !previous) return null; // 전일대비 2건 필요
  return {
    kind,
    current: current.price,
    delta: current.price - previous.price,
    date: current.date,
  };
}

export function buildMarketItems(smp: SmpPoint[], rec: RecPoint[]): MarketItem[] {
  const items: MarketItem[] = [];
  const smpItem = toMarketItem(
    'smp',
    smp.map((point) => ({ date: point.date, price: point.avg })),
  );
  if (smpItem) items.push(smpItem);
  const recItem = toMarketItem(
    'rec',
    rec.map((point) => ({ date: point.date, price: point.landAvgPrice })),
  );
  if (recItem) items.push(recItem);
  return items;
}
